/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { getCurrentUser, isUserManagerByInitiativeId } from '../../selectors/user';
import { getQuestionProgress } from '../../selectors/survey';
import { ROUTES } from '../../constants/routes';
import { DashboardRow } from '../dashboard';
import { loadSurvey, unloadSurveyListSummary } from '../../actions/survey';
import { reloadSurveyList } from '../../slice/initiativeSurveyListSlice';
import { reloadInitiative } from '../../actions/initiative';
import { useAppSelector } from '../../reducers';
import { SurveyContext, SurveyContextLoadedProps } from './SurveyContainer';
import { ReportSwitcherContainer } from '../initiative/ReportSwitcherContainer';
import { CompleteButton } from '../survey/button/CompleteButton';
import { useHistory, useParams } from 'react-router-dom';
import { generateUrl } from '../../routes/util';
import './style.scss';
import SurveyProgress, { AnimationVariant } from '../survey-progress';

interface SurveyOverviewHeaderProps {
  initiativeId: string;
}

export default function SurveyOverviewHeader(props: SurveyOverviewHeaderProps) {
  const { initiativeId } = props;

  const { surveyData } = useContext(SurveyContext) as SurveyContextLoadedProps;

  const history = useHistory();
  const dispatch = useDispatch();
  const { surveyId = '' } = useParams<{ surveyId?: string }>();

  const progressStats = useAppSelector(getQuestionProgress);
  const isUserAdmin = useAppSelector((state) => {
    if (!state.survey.loaded) {
      return false;
    }
    const currentUser = getCurrentUser(state);
    if (currentUser && state.survey.data.roles?.admin?.includes(currentUser._id)) {
      return true;
    }
    return isUserManagerByInitiativeId(state, state.survey.data.initiativeId);
  })

  const handleReload = async (): Promise<void> => {
    if (surveyData._id) {
      await Promise.all([
        dispatch(loadSurvey(surveyData._id, true, true)),
        dispatch(unloadSurveyListSummary()),
        dispatch(reloadSurveyList()),
        dispatch(reloadInitiative())
      ]);
    }
  }

  const onReportChange = useCallback((surveyId: string) => {
    history.push(
      generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, { initiativeId, surveyId })
    );
  }, [history, initiativeId]);

  return (
    <>
      <DashboardRow className='survey-settings-header-title'>
        <div className='flex-fill px-3'>
          <SurveyProgress {...progressStats} animationVariant={AnimationVariant.Animated} />
          <div className='caption text-right text-ThemeTextMedium mt-1'>
            <span>answered {progressStats.completed}/{progressStats.count}</span>
            <span className='px-3'>|</span>
            <span>verified {progressStats.verified}/{progressStats.count}</span>
          </div>
        </div>
        <div>
          <CompleteButton survey={surveyData} isUserAdmin={isUserAdmin} handleReload={handleReload} />
        </div>
        <div>
          <ReportSwitcherContainer
            initiativeId={initiativeId}
            selectedSurveyId={surveyId}
            onChange={onReportChange}
            showAutomaticReport={false}
          />
        </div>
      </DashboardRow>
    </>
  );
}
